// src/hooks/useAuth.js
import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState({ token: null, user: null });
    const [loading, setLoading] = useState(true); 
    const navigate = useNavigate();
    
    // CORREÇÃO: useRef para garantir que o ping só ocorra 1 vez por sessão/refresh
    const accessNotified = useRef(false);

    const fetchAuthUser = useCallback(() => {
        setLoading(true); 
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decodedUser = jwtDecode(token);

                console.log("AuthContext: Token decodificado:", decodedUser);

                if (decodedUser.exp * 1000 < Date.now()) {
                    console.log("AuthContext: Token expirado. Limpando e redirecionando para login.");
                    localStorage.removeItem('token');
                    setAuth({ token: null, user: null });
                    accessNotified.current = false; // Reseta o controle
                    navigate('/login');
                    setLoading(false); 
                    return;
                }
                
                const userWithNormalizedOrgId = {
                    ...decodedUser,
                    organizationId: decodedUser.companyId
                };

                setAuth({ token: token, user: userWithNormalizedOrgId });

                // --- BLOCO DE CORREÇÃO DE LOG DUPLICADO ---
                // Só dispara se accessNotified.current for falso
                if (!accessNotified.current) {
                    api.post('/users/access-notify')
                        .then(() => {
                            console.log("AuthContext: Acesso registrado com sucesso (Ping).");
                        })
                        .catch(err => {
                            console.error("AuthContext: Erro silencioso ao registrar acesso:", err);
                        });
                    
                    // Marca como verdadeiro para não disparar de novo nesse refresh
                    accessNotified.current = true;
                }
                // -------------------------------------------

                console.log("AuthContext: Usuário definido no estado do contexto:", userWithNormalizedOrgId);

            } catch (error) {
                console.error("AuthContext: Erro ao decodificar token ou token inválido:", error);
                localStorage.removeItem('token');
                setAuth({ token: null, user: null });
                accessNotified.current = false;
                navigate('/login');
            } finally {
                setLoading(false); 
            }
        } else {
            console.log("AuthContext: Nenhum token encontrado no localStorage.");
            setAuth({ token: null, user: null });
            setLoading(false); 
        }
    }, [navigate]);

    useEffect(() => {
        console.log("AuthContext: useEffect disparado. Chamando fetchAuthUser.");
        fetchAuthUser();
    }, [fetchAuthUser]);

    const setUserAvatar = (newAvatarUrl) => {
        setAuth(prevAuth => ({
            ...prevAuth,
            user: prevAuth.user ? { ...prevAuth.user, avatarUrl: newAvatarUrl } : null
        }));
    };

    const updateUserProfile = (updatedFields) => {
        setAuth(prevAuth => ({
            ...prevAuth,
            user: prevAuth.user ? { ...prevAuth.user, ...updatedFields } : null
        }));
    };

    const login = (token) => {
        localStorage.setItem('token', token);
        // Resetamos o notified para garantir que o login explícito registre o acesso
        accessNotified.current = false; 
        console.log("AuthContext: Login chamado. Token salvo. Revalidando auth.");
        fetchAuthUser(); 
    };

    const logout = () => {
        console.log("AuthContext: Logout chamado. Limpando token e redirecionando.");
        localStorage.removeItem('token');
        setAuth({ token: null, user: null });
        // Importante: Reseta o controle para o próximo login
        accessNotified.current = false; 
        navigate('/login');
    };

    const revalidateAuth = useCallback(() => {
        console.log("AuthContext: revalidateAuth chamado.");
        fetchAuthUser();
    }, [fetchAuthUser]);

    const contextValue = {
        user: auth.user,
        token: auth.token,
        loading, 
        isAuthenticated: !!auth.user && !!auth.token && !loading, 
        login,
        logout,
        setUserAvatar,
        updateUserProfile,
        revalidateAuth,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {loading ? <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Carregando autenticação...</div> : children}
        </AuthContext.Provider>
    );
};

const useAuth = () => {
    return useContext(AuthContext);
};

export default useAuth;